import { motion } from "motion/react"
import { ArrowRight, Check, ImagePlus, ShieldTick, Tool02 } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import type { PublicShopProfile } from "@/lib/api/public-consumer"
import { AnnotatedVehicle } from "./annotated-vehicle"
import {
  SEVERITY_LABEL,
  type DamageReadout,
  type DetectedArea,
  type Severity,
} from "./summarize"
import type { DamageZone } from "./types"
import { CONSUMER_V2 } from "./copy"

// The white payoff panel that slides up over the pine RevealStage: the AI's
// read cascading in as area rows — confident, fast, and NO price. A small
// silhouette plays a secondary "where on the car" role. DetectionBracket +
// AreaRow are exported for the stage's hero.

const DOT: Record<Severity, string> = {
  minor: "bg-emerald-500",
  moderate: "bg-amber-500",
  severe: "bg-rose-500",
}
const OUTCOME: Record<Severity, string> = {
  minor: "Looks mostly cosmetic. Usually a quick turnaround.",
  moderate: "Repairable damage, a typical body-shop job.",
  severe: "Looks significant. Worth a close look by the shop.",
}

export function DamagePanel({
  readout,
  shop,
  onPrimary,
  onAddPhoto,
}: {
  readout: DamageReadout
  shop: PublicShopProfile
  onPrimary: () => void
  onAddPhoto: () => void
}) {
  const detected: Partial<Record<DamageZone, Severity>> = {}
  const rank: Record<Severity, number> = { minor: 1, moderate: 2, severe: 3 }
  for (const a of readout.areas) {
    if (!a.zone) continue
    const cur = detected[a.zone]
    if (!cur || rank[a.severity] > rank[cur]) detected[a.zone] = a.severity
  }
  const hasZones = Object.keys(detected).length > 0
  const n = readout.areas.length

  return (
    <div>
      <p className="text-[17px] font-bold leading-tight tracking-tight text-zinc-900">
        {readout.headline}
      </p>
      <p className="mt-1 text-[13.5px] leading-relaxed text-zinc-500">
        We found {n} {n === 1 ? "area" : "areas"} to look at. {OUTCOME[readout.worst]}
      </p>

      {/* Per-area list — cascades in. No cost. */}
      <div className="mt-4 space-y-2">
        {readout.areas.map((area, i) => (
          <AreaRow key={i} area={area} index={i} />
        ))}
      </div>

      {hasZones && (
        <details className="mt-4 rounded-2xl border border-zinc-200/70 bg-white">
          <summary className="cursor-pointer list-none px-4 py-3 text-[14px] font-semibold text-zinc-700 marker:hidden">
            See it on your car
          </summary>
          <div className="border-t border-zinc-100 bg-gradient-to-b from-sky-50/30 via-white to-zinc-100 p-3">
            <div className="mx-auto max-w-[220px]">
              <AnnotatedVehicle detected={detected} />
            </div>
          </div>
        </details>
      )}

      <div className="mt-4 flex items-start gap-2.5 rounded-2xl bg-zinc-50 px-4 py-3.5">
        <ShieldTick className="mt-0.5 size-4 shrink-0 text-zinc-400" aria-hidden />
        <p className="text-[14px] leading-snug text-zinc-600">
          {CONSUMER_V2.reveal.freeLine(shop.name)}
        </p>
      </div>

      <div className="mt-5 space-y-2">
        <button
          type="button"
          onClick={onPrimary}
          className="flex h-14 w-full items-center justify-center gap-2.5 rounded-2xl bg-zinc-900 text-[15.5px] font-semibold text-white shadow-xl shadow-zinc-900/20 transition-all duration-150 hover:bg-black hover:shadow-zinc-900/30"
        >
          {CONSUMER_V2.reveal.ctaGood}
          <ArrowRight className="size-4" aria-hidden />
        </button>
        <button
          type="button"
          onClick={onAddPhoto}
          className="flex h-12 w-full items-center justify-center gap-1.5 rounded-xl text-[13.5px] font-semibold text-zinc-500 transition-colors duration-150 hover:text-zinc-900"
        >
          <ImagePlus className="size-4" aria-hidden />
          {CONSUMER_V2.reveal.ctaBad}
        </button>
      </div>
    </div>
  )
}

// Camera-AF style bracket that scales in and pulses once — "the AI found it."
export function DetectionBracket({ label }: { label: string }) {
  const corner = "absolute size-7 border-emerald-400"
  return (
    <motion.div
      initial={{ opacity: 0, scale: 1.25 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.55, type: "spring", stiffness: 240, damping: 18 }}
      className="pointer-events-none absolute left-1/2 top-1/2 h-[46%] w-[58%] -translate-x-1/2 -translate-y-1/2"
    >
      <motion.div
        className="absolute inset-0 rounded-xl"
        initial={{ boxShadow: "0 0 0 0 rgba(16,185,129,0.5)" }}
        animate={{ boxShadow: "0 0 0 8px rgba(16,185,129,0)" }}
        transition={{ delay: 0.7, duration: 0.9 }}
      />
      <span className={cx(corner, "left-0 top-0 rounded-tl-xl border-l-[3px] border-t-[3px]")} />
      <span className={cx(corner, "right-0 top-0 rounded-tr-xl border-r-[3px] border-t-[3px]")} />
      <span className={cx(corner, "bottom-0 left-0 rounded-bl-xl border-b-[3px] border-l-[3px]")} />
      <span className={cx(corner, "bottom-0 right-0 rounded-br-xl border-b-[3px] border-r-[3px]")} />
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9, duration: 0.3 }}
        className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-emerald-400 px-2.5 py-1 text-[13px] font-semibold text-pine shadow-md"
      >
        {label}
      </motion.div>
    </motion.div>
  )
}

export function AreaRow({ area, index }: { area: DetectedArea; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.55 + index * 0.1, duration: 0.3 }}
      className="flex items-center gap-3 rounded-2xl border border-zinc-200/70 bg-white px-4 py-3"
    >
      <span className={cx("size-2.5 shrink-0 rounded-full", DOT[area.severity])} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-semibold leading-tight text-zinc-900">
          {area.label}
        </p>
        <p className="text-[13px] leading-tight text-zinc-600">
          {SEVERITY_LABEL[area.severity]}
          {area.highConfidence && " · high confidence"}
        </p>
      </div>
      {area.action && (
        <span
          className={cx(
            "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[13px] font-semibold",
            area.action === "replace"
              ? "bg-amber-50 text-amber-800"
              : "bg-emerald-50 text-emerald-800",
          )}
        >
          {area.action === "replace" ? (
            <Tool02 className="size-3" aria-hidden />
          ) : (
            <Check className="size-3" aria-hidden />
          )}
          {area.action === "replace" ? "Replace" : "Repair"}
        </span>
      )}
    </motion.div>
  )
}
